import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Accordion from '@repo/ui/accordion'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { PrintIcon, UserTwoIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../../../../api/apis.config'
import { getCenterGroups } from '../../../../../../api/centers/center-groups'
import { diffuseCenterManager } from '../../../../../../api/centers/diffuse-center-manager'
import { getCenter } from '../../../../../../api/centers/get-center'
import { getCenterManager } from '../../../../../../api/centers/get-center-manager'
import { useDownloadDocuments } from '../../../../../../lib/hooks/use-download-documents'
import { useEnrollmentsPickupListCount } from '../../../../../../lib/hooks/use-enrollments-pickup-list-count'
import useTranslate from '../../../../../../lib/hooks/use-translate'
import { AccomodationEnum } from '../../../../../../lib/types/enums/accomodation-type/accomodation-type'
import { affectedStaffCenterPlacementFields } from '../../../../../../lib/types/fields/affected-staffs/affected-staff'
import { AccordionState } from '../../../../../../lib/types/utils'
import { formatPhoneNumber } from '../../../../../../utils/functions/format-phone-number'
import OlCenterAffectedStaffTable from '../../../../ol-common/ol-center-affected-staff-table'
import OlCenterPlacementSummaryBadges, {
  OlCenterPlacementSummaryBadgesRef
} from '../../../../ol-common/ol-center-placement-summary-badges'
import CenterLecCommentTable from './center-comments/center-lec-comment-table'
import CenterOlComments from './center-comments/center-ol-comments'
import CenterStudentsDataTable from './center-students-data-table'

type Props = {
  id: number
}

type Key = 'staffs-data' | 'students-data' | 'comments'

const initialAccordionState = (
  t: TFunction<AvailableLanguages, undefined>
): AccordionState<Key>[] => [
  { key: 'staffs-data', label: t('ol.center.staff.table.title'), open: true },
  { key: 'students-data', label: t('ol.center.enrollment.table.title'), open: true },
  { key: 'comments', label: t('ol.center.enrollment.table.comment.title'), open: true }
]

const CenterPlacementAndDiffusionForParents = ({ id }: Props) => {
  const { t } = useTranslate()
  const { downloadDocuments } = useDownloadDocuments()
  const queryClient = useQueryClient()
  const summaryBadgesRef = useRef<OlCenterPlacementSummaryBadgesRef>(null)
  const [accordionState, setAccordionState] = useState<AccordionState<Key>[]>(
    initialAccordionState(t)
  )

  const diffuseCenterManagerToParents = useMutation({
    mutationKey: ['diffuse-center-manager-to-parents'],
    mutationFn: diffuseCenterManager,
    onSuccess: () => {
      toast.success(t('ol.center.placement.diffusion.center.manager.success'))
      // Invalidate queries to trigger refetch and update UI
      queryClient.invalidateQueries({ queryKey: ['centers', id, 'Basic'] })
      queryClient.invalidateQueries({ queryKey: ['center-manager', id] })
    }
  })

  const { data: center } = useQuery({
    queryKey: ['centers', id, 'Basic'],
    queryFn: () => getCenter(id, 'BASIC'),
    enabled: !!id && !isNaN(id)
  })

  const { data: centerGroups } = useQuery({
    queryKey: ['center-groups', id],
    queryFn: () => getCenterGroups(id)
  })
  const hasCenterGroups = (centerGroups?.total ?? 0) > 0

  const { data: centerManager } = useQuery({
    queryKey: ['center-manager', id, affectedStaffCenterPlacementFields],
    queryFn: () => getCenterManager(id, affectedStaffCenterPlacementFields)
  })

  const phoneNumberForFrenchLeader =
    center?.mobileNumberForFrenchLeader?.phoneNumber ?? centerManager?.facNumber

  // Only France stays without LEC transport have a "who collects the youth" list to print
  const isFranceCenter = center?.journeyPlace?.country?.code === 'FRA'
  const { hasCandidates: hasPickupListCandidates } = useEnrollmentsPickupListCount(
    id,
    isFranceCenter
  )

  const renderAccordionContent = (key: Key) => {
    if (!center) return null
    switch (key) {
      case 'staffs-data':
        return (
          <OlCenterAffectedStaffTable
            centerId={id}
            mode="french-view"
            accomodation={center.accomodation}
            actions={[
              centerManager && center.diffusedCenterManager?.id !== centerManager.id && (
                <Button
                  type="button"
                  loading={diffuseCenterManagerToParents.isPending}
                  variant="ghost"
                  label={t('ol.center.staff.table.diffusion.button')}
                  onClick={() => diffuseCenterManagerToParents.mutate({ centerId: id })}
                />
              )
            ]}
          />
        )
      case 'students-data':
        return (
          !!center && (
            <CenterStudentsDataTable
              data={center}
              onHostFamilyDiffused={() => summaryBadgesRef.current?.refetch()}
            />
          )
        )
      case 'comments':
        return (
          <>
            <CenterOlComments id={Number(id)} />
            <div className={'h-4'} />
            <CenterLecCommentTable id={Number(id)} />
          </>
        )
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col gap-3 mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <Typography
          variant="label-medium"
          color={tailwindColors.black.DEFAULT}
          className="shrink-0"
        >
          {t('ol.center.print.the.students.list')}
        </Typography>
        <Button
          type="button"
          icon={PrintIcon}
          variant="ghost"
          className="shrink-0"
          label={t('ol.center.print.global.list')}
          onClick={() => {
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printFullListOfEnrollments.url, {
                id
              })
            })
          }}
        />
        <Button
          type="button"
          icon={PrintIcon}
          variant="ghost"
          className="shrink-0"
          label={t('ol.center.print.list.by.proram')}
          onClick={() => {
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printPdfListOfEnrollmentsListByProgram.url, {
                id
              })
            })
          }}
        />
        <Button
          type="button"
          icon={PrintIcon}
          variant="ghost"
          className="shrink-0"
          label={t('ol.center.print.list.by.transport.type')}
          onClick={() => {
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printEnrollmentsListByMeansOfTransport.url, {
                id
              })
            })
          }}
        />
        {hasCenterGroups && (
          <Button
            type="button"
            icon={PrintIcon}
            variant="ghost"
            className="shrink-0"
            label={t('ol.center.print.list.by.sub.group')}
            onClick={() => {
              downloadDocuments({
                endpoint: constructApiUrl(
                  API.centers.printPdfListOfEnrollmentsListBySubGroup.url,
                  { id }
                )
              })
            }}
          />
        )}
        {isFranceCenter && (
          <Button
            type="button"
            icon={PrintIcon}
            variant="ghost"
            className="shrink-0"
            disabled={!hasPickupListCandidates}
            label={t('ol.center.print.enrollments.pickup.list')}
            onClick={() => {
              downloadDocuments({
                endpoint: constructApiUrl(API.centers.printEnrollmentsPickupList.url, { id })
              })
            }}
          />
        )}
      </div>
      <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
        {t('ol.center.placement.diffusion.title')}
      </Typography>
      <div className="flex justify-between items-center">
        <div>
          {center?.accomodation === AccomodationEnum.HOST_FAMILY && (
            <OlCenterPlacementSummaryBadges ref={summaryBadgesRef} id={id} />
          )}
        </div>
        <Badge
          variant="white"
          size="flexible"
          className="rounded"
          iconPosition="start"
          icon={<Badge icon={<UserTwoIcon color={tailwindColors.grey.lighter} />} />}
          text={
            <div className="flex gap-1 items-center">
              <Typography variant="label" color={tailwindColors.grey.lighter}>
                {t('ol.center.placement.diffusion.phone.number')} :
              </Typography>
              <Typography variant="label-medium" color={tailwindColors.black.DEFAULT}>
                {valueRender(phoneNumberForFrenchLeader, (phone) => formatPhoneNumber(phone))}
              </Typography>
            </div>
          }
        />
      </div>
      {Object.values(accordionState).map(({ key, open, label }) => (
        <Accordion
          key={key}
          title={label}
          open={open}
          className="bg-white-hover"
          variant="primary"
          onToggle={() =>
            setAccordionState((prev) =>
              prev.map((item) => (item.key === key ? { ...item, open: !item.open } : item))
            )
          }
        >
          {renderAccordionContent(key)}
        </Accordion>
      ))}
    </div>
  )
}

export default CenterPlacementAndDiffusionForParents
